import { ArrowRight } from "lucide-react";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";

// Reusable landing hero. Media is swappable via props without redesign:
// pass mediaType="video" + mediaSrc + poster for a film hero,
// or a responsive `media` object: { webp768, webp1280, fallback, alt,
// desktopPosition, mobilePosition } for art-directed responsive images.
export const LPHero = ({
  label,
  lines = [],
  copy,
  primaryCta = "Start Your Gym Project",
  secondaryCta = "Talk to a Specialist",
  onPrimary,
  onSecondary,
  trustItems = [],
  mediaType = "image",
  mediaSrc,
  media,
  poster,
}) => (
  <section id="top" className="section-darker relative flex min-h-screen items-end overflow-hidden" data-testid="lp-hero">
    <div className="absolute inset-0">
      {mediaType === "video" ? (
        <video className="h-full w-full object-cover" autoPlay muted loop playsInline poster={poster} data-testid="hero-media">
          <source src={mediaSrc} type="video/mp4" />
        </video>
      ) : media ? (
        <picture>
          <source
            type="image/webp"
            srcSet={`${media.webp768} 768w, ${media.webp1280} 1280w${media.webp1920 ? `, ${media.webp1920} 1920w` : ""}`}
            sizes="100vw"
          />
          <img
            src={media.fallback}
            alt={media.alt || ""}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
            style={{ objectPosition: "var(--hero-pos-mobile, 35% 50%)" }}
            data-testid="hero-media"
          />
        </picture>
      ) : (
        <img src={mediaSrc} alt="" className="h-full w-full object-cover" data-testid="hero-media" />
      )}
      {/* YANRE cinematic overlay language — strongest behind the copy (left/bottom),
          progressively lighter toward the equipment/environment (right).
          rtl: mirrors the side gradient so the dark field stays behind the copy. */}
      <div className="absolute inset-0 bg-gradient-to-t from-carbon-950 via-carbon-950/55 to-carbon-950/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-carbon-950/85 via-carbon-950/35 to-transparent rtl:bg-gradient-to-l" />
      <div className="absolute inset-0 bg-carbon-950/20 lg:bg-transparent" />
      <div className="grid-lines absolute inset-0 opacity-30" />
    </div>
    {media && (
      <style>{`
        @media (min-width: 1024px) {
          [data-testid="hero-media"] { object-position: ${media.desktopPosition || "center center"} !important; }
        }
        @media (max-width: 1023px) {
          [data-testid="hero-media"] { object-position: ${media.mobilePosition || "35% 50%"} !important; }
        }
      `}</style>
    )}
    <div className="container-edge relative pb-16 pt-36 lg:pb-24">
      {label && (
        <Reveal>
          <div className="mb-8 flex items-center gap-4">
            <span className="h-px w-10 bg-signal" />
            <span className="overline">{label}</span>
          </div>
        </Reveal>
      )}
      <h1 className="max-w-6xl font-display text-5xl font-extrabold uppercase leading-[0.88] tracking-tight text-white sm:text-7xl lg:text-8xl xl:text-9xl">
        <RevealLines lines={lines} />
      </h1>
      {copy && (
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-titanium/80 sm:text-lg">
            {copy}
          </p>
        </Reveal>
      )}
      <Reveal delay={0.3}>
        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
          <button type="button" onClick={onPrimary} className="btn-signal group" data-testid="hero-primary-cta">
            {primaryCta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            type="button"
            onClick={onSecondary}
            className="btn-ghost border-titanium/30 bg-carbon-950/40 text-titanium backdrop-blur-sm hover:border-signal hover:text-white"
            data-testid="hero-secondary-cta"
          >
            {secondaryCta}
          </button>
        </div>
      </Reveal>
      {trustItems.length > 0 && (
        <Reveal delay={0.4}>
          <div className="mt-14 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-6" data-testid="hero-trust-items">
            {trustItems.map((t) => (
              <span key={t} className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-titanium/60 sm:text-[11px]">
                <span className="h-1.5 w-1.5 bg-signal" />
                {t}
              </span>
            ))}
          </div>
        </Reveal>
      )}
    </div>
  </section>
);
